import { useEffect, useState } from "react";

import { breakpoints, type BreakpointKey } from "../../tokens";
import { resolveBreakpoint } from "./helper";

/**
 * Reactive breakpoint hook for web. Returns the current breakpoint key
 * derived from `window.innerWidth` and re-renders on viewport changes via
 * `matchMedia` listeners (one per breakpoint boundary).
 *
 * @example
 * const bp = useBreakpoint();
 * const isMobile = bp === "sm";
 */
export function useBreakpoint(): BreakpointKey {
  const getCurrent = () =>
    typeof window === "undefined" ? "sm" : resolveBreakpoint(window.innerWidth);

  const [bp, setBp] = useState<BreakpointKey>(getCurrent);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const update = () => setBp(resolveBreakpoint(window.innerWidth));
    const queries = (["md", "lg", "xl"] as const).map((key) =>
      window.matchMedia(`(min-width: ${breakpoints[key]}px)`),
    );

    queries.forEach((q) => q.addEventListener("change", update));
    update();
    return () => queries.forEach((q) => q.removeEventListener("change", update));
  }, []);

  return bp;
}
